import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PowerSettingsNew as PowerIcon
} from '@mui/icons-material'
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import ApiOutAccessB2b from 'api/b2b/ApiOutAccessManag'
import OrbitSession from 'hooks/useCookies'

import { TableRowData } from './SearchAPIKey'

// Action dropdown component
const ActionDropdown = ({
  row,
  onStatusUpdate
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any
  onStatusUpdate: (id: string, newStatus: boolean) => void
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const company = row.original
  const token = OrbitSession.get('faberToken')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Mutation for toggling status
  const { mutate, isLoading } = useMutation({
    mutationFn: async (nextStatus: boolean) => {
      return ApiOutAccessB2b.updateApiOutAccessKeyStatus(
        {
          id: company.id,
          updated_by: OrbitSession.get('email') || '',
          status: !nextStatus
        },
        token
      )
    },
    onSuccess: (_res, nextStatus) => {
      // Update the local state through callback
      onStatusUpdate(company.id, !nextStatus)

      // Invalidate the cache for search results
      queryClient.invalidateQueries({
        queryKey: ['tenant-search'],
        refetchType: 'none'
      })

      toast.success(
        `Key ${!nextStatus ? 'activated' : 'deactivated'} successfully`,
        { autoClose: 2000, hideProgressBar: false }
      )
    },
    onError: () => {
      toast.error('Failed to update status')
    }
  })

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEdit = () => {
    navigate('/hq/b2b/api-out-access/add?mode=edit&id=' + company.companyId)
    handleClose()
  }

  const handleToggleStatus = () => {
    row.original
    mutate(row.original.status)
    handleClose()
  }

  return (
    <>
      <IconButton aria-label='more' onClick={handleClick} size='small'>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleToggleStatus} disabled={isLoading}>
          <PowerIcon sx={{ mr: 1, fontSize: 18 }} />
          {isLoading ? 'Updating' : company.status ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Menu>
    </>
  )
}

// Status chip component
const StatusChip = ({ status }: { status: boolean }) => {
  const isActive = status
  return (
    <Chip
      label={status ? 'Active' : 'Inactive'}
      color={isActive ? 'success' : 'error'}
      size='small'
      variant='filled'
    />
  )
}

interface ApiOutAccessTableProps {
  tenantAccessData: TableRowData[]
  setTenantAccessData: React.Dispatch<React.SetStateAction<TableRowData[]>>
}

export default function ApiOutAccessTable({
  tenantAccessData,
  setTenantAccessData
}: ApiOutAccessTableProps) {
  // Add a force re-render trigger
  const [tableKey, setTableKey] = useState(0)

  // Callback function to update status in parent state
  const handleStatusUpdate = (id: string, newStatus: boolean) => {
    setTenantAccessData((prevState) => {
      const newState = prevState.map((company) => {
        if (company.id === id) {
          return { ...company, status: newStatus }
        }
        return company
      })
      // Force table re-render
      setTableKey((prev) => prev + 1)
      return newState
    })
  }

  // Column helper for type safety
  const columnHelper = createColumnHelper<TableRowData>()

  // Define columns to match your data structure
  const columns = [
    columnHelper.accessor('name', {
      header: 'Company Name',
      cell: (info) => (
        <Typography variant='body2' fontWeight='medium'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('companyId', {
      header: 'Company ID',
      cell: (info) => (
        <Typography variant='body2' fontFamily='monospace'>
          {info.getValue()}
        </Typography>
      )
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => <StatusChip status={info.getValue()} />
    }),
    columnHelper.accessor('createdBy', {
      header: 'Created By',
      cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
    }),
    columnHelper.accessor('createdOn', {
      header: 'Created On',
      cell: (info) => {
        const date = info.getValue()
        // Handle both ISO string and regular date formats
        const formattedDate = new Date(date).toLocaleDateString()
        return (
          <Typography variant='body2' color='text.secondary'>
            {formattedDate}
          </Typography>
        )
      }
    }),
    columnHelper.accessor('updatedBy', {
      header: 'Updated By',
      cell: (info) => <Typography variant='body2'>{info.getValue()}</Typography>
    }),
    columnHelper.accessor('updatedOn', {
      header: 'Updated On',
      cell: (info) => {
        const date = info.getValue()
        // Handle both ISO string and regular date formats
        const formattedDate = new Date(date).toLocaleDateString()
        return (
          <Typography variant='body2' color='text.secondary'>
            {formattedDate}
          </Typography>
        )
      }
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <ActionDropdown row={row} onStatusUpdate={handleStatusUpdate} />
      )
    })
  ]

  // Use the prop data instead of hardcoded data
  const table = useReactTable({
    data: tenantAccessData, // Changed from hardcoded 'data' to 'tenantAccessData'
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.companyId
  })

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} elevation={1} key={tableKey}>
        <Table
          key={
            tenantAccessData.length +
            tenantAccessData.map((d) => d.status).join('')
          }
        >
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: 'grey.50',
                      py: 2
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} sx={{ py: 2.5 }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Empty state - shows when no data is available */}
        {tenantAccessData.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color='text.secondary' variant='h6'>
              No access data found
            </Typography>
            <Typography color='text.secondary' variant='body2' sx={{ mt: 1 }}>
              Search for a company to view their access information
            </Typography>
          </Box>
        )}
      </TableContainer>
    </Box>
  )
}
